import { useEffect, useState } from "react";
import { Ship, LogOut, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, dt, d } from "@/api";
import { Badge, MODE_LABELS } from "@/components/Badge";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function Portal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);

  useEffect(() => { api.get("/portal/operations").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);

  const openFile = async (fileId) => {
    try {
      const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grain min-h-screen bg-white" data-testid="portal-page">
      <header className="flex h-16 items-center justify-between border-b border-zinc-950 px-4 sm:px-8">
        <div className="flex items-center gap-2">
          <Ship className="h-5 w-5 text-klein" strokeWidth={2.5} />
          <span className="font-display text-base font-black tracking-tighter">SEVERGATE<span className="text-klein"> PORTAL</span></span>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden text-sm text-zinc-600 sm:block" data-testid="portal-user">{user?.name}</span>
          <button className="brut-btn-ghost !px-3 !py-1.5 !text-xs" onClick={async () => { await logout(); nav("/giris"); }} data-testid="portal-logout">
            <LogOut className="h-3 w-3" /> Çıkış
          </button>
        </div>
      </header>

      <div className="relative border-b border-zinc-200">
        <img src="https://images.pexels.com/photos/10359129/pexels-photo-10359129.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="Yol üzerinde kamyonlar" className="h-40 w-full object-cover sm:h-56" />
        <div className="absolute bottom-0 left-0 border-r border-t border-zinc-950 bg-white px-6 py-4">
          <p className="overline">Sevkiyatlarınız</p>
          <p className="font-display text-2xl font-black tracking-tighter">Takip ve Belgeler</p>
        </div>
      </div>

      <main className="space-y-4 p-4 sm:p-8">
        {rows.length === 0 && (
          <div className="brut p-10 text-center" data-testid="portal-empty">
            <p className="text-sm text-zinc-600">Şu anda paylaşılan bir sevkiyat bulunmuyor.</p>
          </div>
        )}
        {rows.map((r) => (
          <div key={r.id} className="brut p-4 sm:p-6" data-testid={`portal-operation-${r.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="overline">Dosya No</p>
                <p className="font-mono text-lg font-bold">{r.no}</p>
              </div>
              {r.status && <Badge value={r.status} testId={`portal-status-${r.id}`} />}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 border-t border-zinc-200 pt-4 text-sm sm:grid-cols-4">
              {r.route && <div><p className="overline">Rota</p><p>{r.route}</p></div>}
              {r.mode && <div><p className="overline">Mod</p><p>{MODE_LABELS[r.mode]}</p></div>}
              {r.eta && <div><p className="overline">Tahmini Varış</p><p>{d(r.eta)}</p></div>}
              {r.cargo_desc && <div><p className="overline">Yük</p><p>{r.cargo_desc}</p></div>}
            </div>
            {r.events?.length > 0 && (
              <div className="mt-5">
                <p className="overline">Olay Geçmişi</p>
                <ol className="mt-3 border-l-2 border-zinc-200 pl-4">
                  {r.events.map((e, i) => (
                    <li key={i} className="relative mb-3">
                      <span className="absolute -left-[22px] top-1.5 h-3 w-3 rounded-full border-2 border-klein bg-white" />
                      <p className="text-sm">{e.description}</p>
                      <p className="text-xs text-zinc-500">{dt(e.happened_at)}</p>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {r.documents?.length > 0 && (
              <div className="mt-5">
                <p className="overline">Belgeler</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {r.documents.map((dc) => (
                    <button key={dc.id} className="brut-btn-ghost !px-3 !py-1.5 !text-xs" onClick={() => openFile(dc.file_id)}
                      data-testid={`portal-doc-${dc.id}`}>
                      <Eye className="h-3 w-3" /> {dc.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </main>
    </div>
  );
}
